import { useEffect, useRef, useState } from "react";
import { Loader2, Search, Sparkles } from "lucide-react";
import { Button } from "@/components/ui";
import { useTranslation } from "@/hooks/useTranslation";

/** Typing speed and how long a finished example rests before the next one. */
const TYPE_MS = 45;
const HOLD_MS = 2200;

const EXAMPLE_KEYS = [
  "marketplace.aiSearch.example1",
  "marketplace.aiSearch.example2",
  "marketplace.aiSearch.example3",
  "marketplace.aiSearch.example4",
];

export interface AISearchBarProps {
  onSearch: (query: string) => void;
  loading?: boolean;
}

/**
 * Plain-language creator search.
 *
 * The animated placeholder is the point: a brand does not know this box accepts
 * a whole sentence until it watches one being typed. It carries the idea from
 * the old PropTech AISearchTextarea, rebuilt on the marketplace's light theme —
 * that component is dark-glass with purple gradients and would read as a
 * different product bolted onto this page.
 */
export function AISearchBar({ onSearch, loading = false }: AISearchBarProps) {
  const { t } = useTranslation();
  const [query, setQuery] = useState("");
  const [placeholder, setPlaceholder] = useState("");
  const [exampleIndex, setExampleIndex] = useState(0);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const examples = EXAMPLE_KEYS.map((k) => t(k));

  // Type out one example, hold, then advance. Skipped entirely once the user
  // types — an animation running under their own text is just noise.
  useEffect(() => {
    if (query.length > 0) {
      setPlaceholder("");
      return;
    }

    const target = examples[exampleIndex % examples.length] ?? "";
    let char = 0;
    let advanceTimer: ReturnType<typeof setTimeout> | undefined;

    const typer = setInterval(() => {
      char += 1;
      setPlaceholder(target.slice(0, char));

      if (char >= target.length) {
        clearInterval(typer);
        advanceTimer = setTimeout(() => setExampleIndex((i) => i + 1), HOLD_MS);
      }
    }, TYPE_MS);

    return () => {
      clearInterval(typer);
      if (advanceTimer) clearTimeout(advanceTimer);
    };
    // `examples` is rebuilt every render (t() call), so depending on it would
    // restart the animation constantly. Index + query are the real inputs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exampleIndex, query.length]);

  const submit = () => {
    const text = query.trim();
    if (!text || loading) return;
    onSearch(text);
  };

  return (
    <div className="group relative overflow-hidden rounded-card border border-neutral-200 bg-white px-5 py-[18px] transition-[border-color,box-shadow] hover:border-steel-600/40 hover:shadow-ai-hover focus-within:border-steel-600/40 focus-within:shadow-ai-hover">
      {/* A scan line sweeping the card. It is the only ambient motion on the
          page, and it is what tells someone this box is not a plain search
          field before they have typed anything. */}
      <span
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 h-0.5 w-[34%] animate-sweep bg-gradient-to-r from-transparent via-steel-500 to-transparent"
      />

      <div className="mb-3 flex items-center gap-1.5">
        <Sparkles className="size-3.5 text-steel-600" strokeWidth={2.2} />
        <span className="text-label uppercase tracking-[0.12em] text-steel-600">
          {t("marketplace.aiSearch.badge")}
        </span>
      </div>

      <div className="relative">
        <textarea
          ref={textareaRef}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              submit();
            }
          }}
          rows={2}
          aria-label={t("marketplace.aiSearch.badge")}
          // 21px, not 14. The sentence a brand types here is the primary input
          // on the screen and it used to be set smaller than the hint below it.
          className="w-full resize-none bg-transparent text-[21px] font-medium leading-snug text-neutral-900 outline-none"
        />

        {/* Rendered outside the textarea so the caret sits at the start rather
            than after the animated text. */}
        {!query && (
          <div className="pointer-events-none absolute inset-0 text-[21px] font-medium leading-snug text-neutral-400">
            {placeholder}
            <span className="animate-caret text-steel-500">|</span>
          </div>
        )}
      </div>

      <div className="mt-3 flex items-center justify-between gap-3">
        <p className="text-micro text-neutral-400">{t("marketplace.aiSearch.hint")}</p>
        <Button
          onClick={submit}
          disabled={!query.trim() || loading}
          className="shrink-0"
        >
          {loading ? <Loader2 className="animate-spin" /> : <Search />}
          {t("marketplace.aiSearch.button")}
        </Button>
      </div>
    </div>
  );
}
